import { useState, type FormEvent } from 'react';
import type { Movie } from '@filmsloth/shared';
import { ApiError, api } from '../api/client.js';
import { useMovieTitle, useT } from '../i18n/index.js';
import { trailerErrorKey } from '../i18n/errorKeys.js';
import { useSession } from '../session/useSession.js';

/**
 * Lets a participant supply the trailer for a film that has none.
 *
 * Nothing here decides anything: the server re-derives every fact about the
 * video from YouTube and either takes it or refuses it. What this does own is
 * telling the submitter *why* a link was refused — a rejected trailer is not
 * stored anywhere, so an unexplained failure would just get retried.
 */
export function TrailerSubmitForm({ movie }: { movie: Movie }) {
  const t = useT();
  const movieTitle = useMovieTitle();
  const { credentials } = useSession();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!credentials) return null;

  // The accepted trailer arrives through the session stream a moment later and
  // replaces this form with the player, so the note is all the feedback needed.
  if (done) return <p className="trailer-add__done small">{t('trailerAdd.success')}</p>;

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn--ghost small trailer-add__open"
        onClick={() => setOpen(true)}
      >
        {t('trailerAdd.open')}
      </button>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || url.trim().length === 0) return;

    setBusy(true);
    setError(null);
    try {
      await api.submitTrailer(credentials.token, movie.id, url.trim());
      setDone(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? t(trailerErrorKey(cause.code)) : t('trailerError.unknown'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="trailer-add" onSubmit={submit}>
      <label className="trailer-add__label" htmlFor={`trailer-url-${movie.id}`}>
        {t('trailerAdd.title', { title: movieTitle(movie) })}
      </label>
      <p className="trailer-add__hint small muted">{t('trailerAdd.hint')}</p>
      <input
        id={`trailer-url-${movie.id}`}
        className="input"
        type="url"
        inputMode="url"
        autoComplete="off"
        placeholder={t('trailerAdd.placeholder')}
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        disabled={busy}
      />
      {error && (
        <p className="trailer-add__error small" role="alert">
          {error}
        </p>
      )}
      <div className="trailer-add__actions">
        <button type="submit" className="btn small" disabled={busy || url.trim().length === 0}>
          {busy ? t('trailerAdd.submitting') : t('trailerAdd.submit')}
        </button>
        <button
          type="button"
          className="btn btn--ghost small"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          {t('trailerAdd.cancel')}
        </button>
      </div>
    </form>
  );
}
